/**
 * Supplier profile header: back button, logo (uploadable for those allowed),
 * name, onboarding / approval-level / risk / credit badges, the per-customer
 * active toggle and the Contact button.
 *
 * Split out of SupplierDetail.tsx to keep that page under the 500-line
 * limit; the markup and behaviour are unchanged.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Loader2,
  ArrowLeft,
  Star,
  Mail,
  Camera,
  ShieldAlert,
  ShieldCheck,
  Shield,
  ShieldX,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Company, SupplierProfile } from '../../lib/api';
import { getRiskLevelStyle } from '../suppliers-list/utils';
import { ActiveStatusToggle } from './ActiveStatusToggle';
import { fetchCompanyUsersIfAllowed } from './utils';

interface SupplierHeaderProps {
  supplier: SupplierProfile;
  /** Full Company (what the Overview tab shows). The embedded
   *  supplier.company_details is the slim shape and carries no email. */
  companyInfo: Company | null;
  canEditLogo: boolean;
  isCustomerAdmin: boolean;
  onUploadLogo: (file: File) => Promise<void>;
  /** Re-fetch the profile after the active toggle changes it. */
  onChanged: () => Promise<void> | void;
}

export function SupplierHeader({
  supplier,
  companyInfo,
  canEditLogo,
  isCustomerAdmin,
  onUploadLogo,
  onChanged,
}: SupplierHeaderProps) {
  const navigate = useNavigate();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const onLogoFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be 5MB or smaller');
      return;
    }
    try {
      setUploadingLogo(true);
      await onUploadLogo(file);
      toast.success('Logo updated');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to upload logo';
      toast.error(message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const contactSupplier = async () => {
    // Named contact first, then the company email, then the supplier's own
    // users — the Users tab below listed Jessie Pinkman with an address while
    // this button said there was no contact, because it only ever read
    // company.primary_email. The profile's embedded company_details is the
    // lightweight shape and carries no email; companyInfo is the full company
    // the Overview tab shows, so read it too — the users fallback is
    // admin-only (CR-6069) and 403s for a customer user.
    let email = supplier.primary_contact?.email
      || supplier.company_details.primary_email
      || companyInfo?.primary_email
      || '';
    if (!email) {
      try {
        const users = (await fetchCompanyUsersIfAllowed(supplier.company)) as unknown as Array<{ email?: string; is_active?: boolean; user?: { email?: string; is_active?: boolean } }>;
        const first = users.find((u) => (u.is_active ?? u.user?.is_active ?? true) && (u.email || u.user?.email));
        email = first?.email || first?.user?.email || '';
      } catch (err: unknown) {
        console.error('Error loading supplier users for contact:', err);
      }
    }
    if (email) {
      window.open(`mailto:${email}`, '_blank');
    } else {
      toast.error('No contact email available for this supplier — add a user or a primary contact first.');
    }
  };

  const onboardingStatus = (supplier as unknown as Record<string, unknown>)['onboarding_status'] as string | undefined;
  const logo = (supplier.company_details as unknown as Record<string, unknown>)?.['logo_url'] as string | undefined;

  return (
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/suppliers')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="relative w-24 h-24 rounded-lg border bg-white flex items-center justify-center overflow-hidden shrink-0 p-2 group">
          {logo ? (
            <img src={logo} alt={supplier.company_name} className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-3xl font-semibold text-muted-foreground">
              {supplier.company_name.trim().charAt(0).toUpperCase() || '?'}
            </span>
          )}
          {canEditLogo && (
            <>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onLogoFileChosen}
              />
              <button
                type="button"
                aria-label="Change company logo"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/50 text-white opacity-0 group-hover:opacity-100 transition disabled:opacity-60"
              >
                {uploadingLogo ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Camera className="h-5 w-5 mr-1" />
                    <span className="text-xs font-medium">Change</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
        <div>
          <div className="flex items-center gap-3 mb-2">
            {supplier.is_preferred && (
              <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
            )}
            <h1 className="text-3xl font-bold tracking-tight">{supplier.company_name}</h1>
            <Badge
              className={
                onboardingStatus === 'approved' || supplier.is_approved
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : onboardingStatus === 'not_approved'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  : onboardingStatus === 'pending_initial'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  : onboardingStatus === 'invited'
                  ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  : onboardingStatus === 'due_diligence'
                  ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                  : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
              }
            >
              {(() => {
                switch (onboardingStatus) {
                  case 'approved': return 'Approved';
                  case 'not_approved': return 'Not Approved';
                  case 'pending_initial': return 'Pending Initial Approval';
                  case 'invited': return 'Invited';
                  case 'due_diligence': return 'Due Diligence';
                  case 'draft': return 'Draft';
                  default: return supplier.is_approved ? 'Approved' : 'Under Review';
                }
              })()}
            </Badge>
            {/* The approval level chosen when the supplier was approved — the
                answer to "what is this supplier approved to?" after the
                Level 1/2/3 dialog closes. */}
            {supplier.approval_level && (
              <Badge
                variant="outline"
                className="text-xs font-medium px-2.5 py-1 border-green-300 text-green-800 dark:text-green-200"
                title={supplier.approved_at ? `Approved on ${new Date(supplier.approved_at).toLocaleDateString()}` : undefined}
              >
                {supplier.approval_level_display || `Level ${supplier.approval_level}`}
              </Badge>
            )}
            {/* CR-4771: Risk Level badge — same styling as the cards */}
            {supplier.risk_level && supplier.risk_level !== 'none' && (() => {
              const style = getRiskLevelStyle(supplier.risk_level);
              const Icon = style.icon === 'critical' ? ShieldX
                : style.icon === 'high' ? ShieldAlert
                : style.icon === 'low' ? ShieldCheck
                : Shield;
              return (
                <Badge className={`text-xs font-medium px-2.5 py-1 ${style.badgeColor}`}>
                  <Icon className="h-3.5 w-3.5 mr-1" />
                  {style.label}
                </Badge>
              );
            })()}
            {/* CR-4771: Credit Score badge — banding matches SupplierCard */}
            {typeof supplier.latest_credit_score === 'number' && (() => {
              const score = supplier.latest_credit_score;
              const band = score >= 800
                ? { label: 'Excellent', cls: 'bg-green-50 text-green-700 border-green-200' }
                : score >= 700
                  ? { label: 'Good', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
                  : score >= 600
                    ? { label: 'Fair', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
                    : { label: 'Poor', cls: 'bg-red-50 text-red-700 border-red-200' };
              return (
                <Badge className={`text-xs font-medium px-2.5 py-1 ${band.cls}`} title={`Credit Score ${score} — ${band.label}`}>
                  Credit {score}
                </Badge>
              );
            })()}
            {/* CR-4056 staff company toggle / V9 customer-admin relationship toggle / read-only badge. */}
            <ActiveStatusToggle
              supplier={supplier}
              isCustomerAdmin={isCustomerAdmin}
              onChanged={onChanged}
            />
          </div>
          <p className="text-muted-foreground">
            {supplier.company_details.company_type === 'supplier' ? 'Supplier' : 'Company'} Profile
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={contactSupplier}>
          <Mail className="h-4 w-4 mr-2" />
          Contact
        </Button>
      </div>
    </div>
  );
}
